import { Router } from "@angular/router";
import { watchState } from "@ngrx/signals";
import { ButtonModule } from "primeng/button";
import { NgOptimizedImage } from '@angular/common';
import { InputTextModule } from 'primeng/inputtext';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { AuthState, AuthStore } from "@auth/stores/auth.store";
import { ReactiveFormsModule, FormControl } from '@angular/forms';
import { ForgotPasswordBuildFormService } from '@auth/services/forgot-password-build-form.service';
import { ChangeDetectionStrategy, Component, inject, signal, OnInit, Injector } from '@angular/core';
import {
  APP_BRAND_LOGO_ALT,
  APP_BRAND_NAME,
  APP_BRAND_ORG,
  APP_BRAND_TAGLINE,
} from '@shared/constants/app-brand.const';

@Component({
  selector: 'app-forgot-password',
  imports: [
    ButtonModule,
    InputTextModule,
    IconFieldModule,
    InputIconModule,
    NgOptimizedImage,
    ReactiveFormsModule,
  ],
  templateUrl: './forgot-password.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ForgotPasswordComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly injector = inject(Injector);
  private readonly authStore = inject(AuthStore);
  private readonly forgotPasswordBuildFormService = inject(ForgotPasswordBuildFormService);

  public readonly isSubmitting = signal(false);
  public readonly forgotPasswordForm = this.forgotPasswordBuildFormService.buildForgotPasswordForm();

  readonly brandLogoAlt = APP_BRAND_LOGO_ALT;
  readonly brandName = APP_BRAND_NAME;
  readonly brandTagline = APP_BRAND_TAGLINE;
  readonly brandFooterLine = `${APP_BRAND_NAME} · ${APP_BRAND_ORG}`;

  /**
   * Getter para acceder al control de email del formulario.
   */
  get email(): FormControl {
    return this.forgotPasswordForm.controls['email'] as FormControl;
  }

  /**
   * Método del ciclo de vida Angular que se ejecuta al inicializar el componente.
   */
  public ngOnInit(): void {
    this.redirectIfAuthenticated();
    this.watchAuthStateChanges();
  }

  /**
   * Maneja el evento de envío del formulario de recuperación de contraseña.
   */
  public onSubmit(): void {
    if (this.forgotPasswordForm.invalid) return;
    this.authStore.forgotPassword(this.forgotPasswordForm.value);
  }

  /**
   * Navega de regreso a la página de login.
   */
  public goBack(): void {
    this.router.navigate(['/auth/login']);
  }

  /**
   * Redirige al usuario si ya está autenticado.
   */
  private redirectIfAuthenticated(): void {
    if (this.authStore.isAuthenticated()) {
      this.router.navigate(['/app/inicio']);
    }
  }

  /**
   * Observa los cambios de estado en el store de autenticación.
   */
  private watchAuthStateChanges(): void {
    watchState(this.authStore, this.handleAuthStateChange.bind(this), {
      injector: this.injector
    });
  }

  /**
   * Maneja los cambios de estado de autenticación.
   */
  private handleAuthStateChange(authState: AuthState): void {
    if (authState.isLoading) {
      this.clearForm();
    }
    this.isSubmitting.set(authState.isLoading);
  }

  /**
   * Limpia el formulario restableciéndolo a sus valores iniciales.
   */
  private clearForm(): void {
    const initialValues = this.forgotPasswordBuildFormService.buildForgotPasswordForm().getRawValue();
    this.forgotPasswordForm.reset(initialValues);
  }
}
